import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { hasCustomRequestListeners, notifyForbiddenListeners, notifyRequestStarted } from '#api/apiListeners'
import { buildAuthorizationHeader } from '#api/buildAuthorizationHeader'
import { getSessionState } from '#features/User/userManager'
import type { enhancedTmsApiService } from './enhancements/enhancedTmsApiService'

const baseUrl = '/api'

const fetchConfig = {
  baseUrl,
  prepareHeaders: (headers: Headers) => {
    const session = getSessionState()
    const authHeader = buildAuthorizationHeader(session.user)
    if (authHeader) {
      headers.set('Authorization', authHeader.Authorization)
    }
    if (VITE_EXTRA_HEADERS && typeof VITE_EXTRA_HEADERS === 'object') {
      for (const [key, value] of Object.entries(VITE_EXTRA_HEADERS)) {
        if (typeof value !== 'string' || typeof key !== 'string') {
          throw new Error(
            `Injected extra headers are not of expected type, [key, value]: [${JSON.stringify(key)}, ${JSON.stringify(value)}]\nHeaders object:\n${JSON.stringify(VITE_EXTRA_HEADERS)}`,
          )
        }
        headers.set(key, value)
      }
    }
    return headers
  },
  responseHandler: async (response: Response) => {
    if (response.ok) {
      if (response.headers.get('Content-Type')?.includes('text/html')) {
        throw new Error('Expected JSON response but received HTML')
      }

      return response.json()
    } else {
      if (response.status === 403) {
        notifyForbiddenListeners(response)
      }

      if (response.headers.get('Content-Type')?.includes('application/json')) {
        return response.json()
      }

      if (response.headers.get('Content-Type')?.includes('text/html')) {
        return 'HTML response'
      }

      // Fallback to text response for non-JSON errors
      return response.text()
    }
  },
}

function makeFetchFn(customResponseHandler?: (input: RequestInfo, init: RequestInit | undefined) => void) {
  if (customResponseHandler) {
    return fetchBaseQuery({
      ...fetchConfig,
      fetchFn: async (input, init) => {
        customResponseHandler(input, init)
        return await fetch(input, init)
      },
    })
  }

  return fetchBaseQuery(fetchConfig)
}

const defaultFetchFn = makeFetchFn()

export const tmsApi = createApi({
  reducerPath: 'tmsApi',
  baseQuery: async (args, api, extraOptions) => {
    try {
      const shouldNotify = hasCustomRequestListeners()
      const usedFetcher = shouldNotify
        ? makeFetchFn(async (input, init) => {
            let bodyString: string | null = null
            try {
              // this will consume the body stream, so we need to clone the request
              bodyString = typeof input === 'string' ? null : await input.clone().text()
            } catch {
              // ignore body parsing errors
            }

            if (bodyString && bodyString.length > 2048) {
              bodyString = `${bodyString.substring(0, 2048)}... (truncated)`
            }

            notifyRequestStarted({
              endpoint: api.endpoint,
              url: typeof input === 'string' ? input : input.url,
              method: typeof input === 'string' ? (init?.method ?? 'GET') : (input.method ?? 'GET'),
              startedAt: performance.now(),
              body: bodyString,
              args: args,
            })
          })
        : defaultFetchFn

      const response = await usedFetcher(args, api, extraOptions)
      if (response.error) {
        Object.assign(response.error, {
          queryData: {
            method: response.meta?.request.method ?? 'GET',
            body: response.meta?.request.body ?? null,
            endpoint: `${baseUrl}/${api.endpoint}`,
            url: response.meta?.request.url,
          },
        })
      }
      return response
    } catch (error) {
      return { error }
    }
  },
  endpoints: () => ({}),
})

export type TmsApi = typeof enhancedTmsApiService
